import { contactListsApi } from '@/api';
import { queryKeys } from '@/api/query-keys.ts';
import {
  AddDefaultContactsResponse,
  BulkAddContactsRequest,
  ContactList,
  ContactListUpdateRequest,
  ContactListCreateRequest,
} from '@/api/contact-list/contact-list.types.ts';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

export const useContactLists = (
  options?: Partial<UseQueryOptions<ContactList[], AxiosError>>,
) =>
  useQuery<ContactList[], AxiosError>({
    queryKey: queryKeys.contactLists.all,
    queryFn: () => contactListsApi.getContactLists().then((res) => res.data),
    ...options,
  });

export const useCreateContactList = (
  options?: UseMutationOptions<
    ContactList,
    AxiosError,
    ContactListCreateRequest
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<ContactList, AxiosError, ContactListCreateRequest>({
    mutationFn: (body) =>
      contactListsApi.createContactList(body).then((res) => res.data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contactLists.all });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateContactList = (
  options?: UseMutationOptions<
    ContactList,
    AxiosError,
    { listId: number; body: ContactListUpdateRequest }
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<
    ContactList,
    AxiosError,
    { listId: number; body: ContactListUpdateRequest }
  >({
    mutationFn: ({ listId, body }) =>
      contactListsApi.updateContactList(listId, body).then((res) => res.data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contactLists.all });
      options?.onSuccess?.(...args);
    },
  });
};

export interface AddDefaultContactsArgs {
  listId: number;
  scope?: 'own' | 'all';
}

export const useAddDefaultContactsToContactList = (
  options?: UseMutationOptions<
    AddDefaultContactsResponse,
    AxiosError,
    AddDefaultContactsArgs
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<
    AddDefaultContactsResponse,
    AxiosError,
    AddDefaultContactsArgs
  >({
    mutationFn: ({ listId, scope = 'own' }) =>
      contactListsApi
        .addDefaultContacts(listId, scope)
        .then((res) => res.data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contactLists.all });
      options?.onSuccess?.(...args);
    },
  });
};

export interface BulkAddContactsArgs {
  listId: number;
  body: BulkAddContactsRequest;
}

/**
 * Unified bulk-import for the Verteiler detail's "Kontakte importieren" dialog:
 * adds contacts from a chosen source (default contact / all contacts) + scope
 * (own / all), optionally filtered to those with a Mailempfang (stocklist)
 * consent.
 */
export const useBulkAddContactsToContactList = (
  options?: UseMutationOptions<
    AddDefaultContactsResponse,
    AxiosError,
    BulkAddContactsArgs
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<AddDefaultContactsResponse, AxiosError, BulkAddContactsArgs>(
    {
      mutationFn: ({ listId, body }) =>
        contactListsApi.bulkAddContacts(listId, body).then((res) => res.data),
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.contactLists.all });
        options?.onSuccess?.(...args);
      },
    },
  );
};

export const useDeleteContactList = (
  options?: UseMutationOptions<void, AxiosError, number>,
) => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, number>({
    mutationFn: (listId) =>
      contactListsApi.deleteContactList(listId).then((res) => res.data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contactLists.all });
      options?.onSuccess?.(...args);
    },
  });
};
